/**
 * SessionManager — JWT-based sessions with idle timeout and step-up auth
 *
 * Covers TODO Phase 13.2:
 *  - JWT access token (15-min expiry) + refresh token (7-day, rotate on use)
 *  - HttpOnly, Secure, SameSite cookie config (enforced server-side; headers set here)
 *  - 30-minute idle timeout → session expired → re-login
 *  - Step-up authentication for privileged actions
 *
 * In this client-side demo, tokens are simulated with sessionStorage.
 * In production, access tokens come from the auth server and refresh via httpOnly cookies.
 */

import CryptoJS from 'crypto-js';
import { logSecurityEvent } from '../../utils/auditLogger';

const ACCESS_TOKEN_KEY = 'medfast.access_token';
const REFRESH_TOKEN_KEY = 'medfast.refresh_token';
const SESSION_META_KEY = 'medfast.session_meta';
const ACTIVITY_KEY = 'medfast.last_activity';

const ACCESS_TOKEN_EXPIRY_MS = 15 * 60 * 1000;       // 15 minutes
const REFRESH_TOKEN_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;              // 30 minutes
const STEP_UP_DURATION_MS = 5 * 60 * 1000;           // 5 minutes

let _idleTimer = null;
let _tokenRefreshTimer = null;
let _onSessionExpired = null;

class SessionManager {
  // ── Session lifecycle ─────────────────────────────────────────────

  createSession(user) {
    const now = Date.now();

    const accessToken = this._generateToken({
      sub: user.id,
      role: user.role,
      type: 'access',
      iat: now,
      exp: now + ACCESS_TOKEN_EXPIRY_MS,
    });

    const refreshToken = this._generateToken({
      sub: user.id,
      type: 'refresh',
      iat: now,
      exp: now + REFRESH_TOKEN_EXPIRY_MS,
    });

    const meta = {
      userId: user.id,
      role: user.role,
      createdAt: new Date(now).toISOString(),
      accessExpiry: new Date(now + ACCESS_TOKEN_EXPIRY_MS).toISOString(),
      refreshExpiry: new Date(now + REFRESH_TOKEN_EXPIRY_MS).toISOString(),
      stepUpGrantedAt: null,
    };

    try {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      sessionStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));
      this._recordActivity();
    } catch (e) {
      console.error('[SessionManager] Failed to persist session', e);
    }

    this._startIdleTimer();
    this._startTokenRefresh();

    return { accessToken, refreshToken };
  }

  destroySession() {
    this._stopIdleTimer();
    this._stopTokenRefresh();

    try {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(SESSION_META_KEY);
      sessionStorage.removeItem(ACTIVITY_KEY);
    } catch { /* noop */ }
  }

  // ── Token access ──────────────────────────────────────────────────

  getAccessToken() {
    try {
      const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      if (!token) return null;

      const payload = this._decodeToken(token);
      if (!payload || payload.exp < Date.now()) {
        return this.refreshAccessToken() ? sessionStorage.getItem(ACCESS_TOKEN_KEY) : null;
      }
      return token;
    } catch {
      return null;
    }
  }

  refreshAccessToken() {
    try {
      const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) return false;

      const payload = this._decodeToken(refreshToken);
      if (!payload || payload.exp < Date.now()) {
        this._handleSessionExpired('refresh_token_expired');
        return false;
      }

      const now = Date.now();
      const newAccess = this._generateToken({
        sub: payload.sub,
        role: this._getMeta()?.role,
        type: 'access',
        iat: now,
        exp: now + ACCESS_TOKEN_EXPIRY_MS,
      });

      const newRefresh = this._generateToken({
        sub: payload.sub,
        type: 'refresh',
        iat: now,
        exp: now + REFRESH_TOKEN_EXPIRY_MS,
      });

      sessionStorage.setItem(ACCESS_TOKEN_KEY, newAccess);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);

      const meta = this._getMeta();
      if (meta) {
        meta.accessExpiry = new Date(now + ACCESS_TOKEN_EXPIRY_MS).toISOString();
        meta.refreshExpiry = new Date(now + REFRESH_TOKEN_EXPIRY_MS).toISOString();
        sessionStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));
      }

      return true;
    } catch {
      return false;
    }
  }

  // ── Idle timeout ──────────────────────────────────────────────────

  onSessionExpired(callback) {
    _onSessionExpired = callback;
  }

  recordActivity() {
    this._recordActivity();
    this._resetIdleTimer();
  }

  isIdle() {
    const last = this._getLastActivity();
    if (!last) return true;
    return Date.now() - last > IDLE_TIMEOUT_MS;
  }

  getIdleStatus() {
    const last = this._getLastActivity();
    if (!last) return { idle: true, remainingMs: 0 };
    const elapsed = Date.now() - last;
    return {
      idle: elapsed > IDLE_TIMEOUT_MS,
      remainingMs: Math.max(0, IDLE_TIMEOUT_MS - elapsed),
      elapsedMs: elapsed,
    };
  }

  // ── Step-up authentication ────────────────────────────────────────

  grantStepUp() {
    const meta = this._getMeta();
    if (meta) {
      meta.stepUpGrantedAt = new Date().toISOString();
      sessionStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));
    }
  }

  hasStepUp() {
    const meta = this._getMeta();
    if (!meta?.stepUpGrantedAt) return false;
    const grantedAt = new Date(meta.stepUpGrantedAt).getTime();
    return Date.now() - grantedAt < STEP_UP_DURATION_MS;
  }

  // ── Cookie configuration (headers for production server) ──────────

  static getCookieConfig() {
    return {
      httpOnly: true,
      secure: true,
      sameSite: 'Strict',
      path: '/',
      maxAge: REFRESH_TOKEN_EXPIRY_MS / 1000,
    };
  }

  static getCSPHeaders() {
    return {
      'Content-Security-Policy': [
        "default-src 'self'",
        "script-src 'self'",
        "style-src 'self' 'unsafe-inline'",
        "img-src 'self' data: https:",
        "connect-src 'self' https://*.medfastcrm.com",
        "frame-ancestors 'none'",
        "base-uri 'self'",
      ].join('; '),
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
    };
  }

  // ── TLS enforcement config ────────────────────────────────────────

  static getTLSConfig() {
    return {
      minVersion: 'TLSv1.3',
      ciphers: 'TLS_AES_256_GCM_SHA384:TLS_CHACHA20_POLY1305_SHA256:TLS_AES_128_GCM_SHA256',
      honorCipherOrder: true,
      certificateManagement: 'ACM',
    };
  }

  // ── Internal ──────────────────────────────────────────────────────

  _generateToken(payload) {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const body = btoa(JSON.stringify(payload));
    const signature = CryptoJS.HmacSHA256(`${header}.${body}`, this._getSigningKey()).toString();
    return `${header}.${body}.${signature}`;
  }

  _decodeToken(token) {
    try {
      const [, body] = token.split('.');
      return JSON.parse(atob(body));
    } catch {
      return null;
    }
  }

  _getSigningKey() {
    return 'medfast-jwt-demo-key-replace-in-prod';
  }

  _getMeta() {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_META_KEY));
    } catch {
      return null;
    }
  }

  _recordActivity() {
    try {
      sessionStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    } catch { /* noop */ }
  }

  _getLastActivity() {
    try {
      const val = sessionStorage.getItem(ACTIVITY_KEY);
      return val ? parseInt(val, 10) : null;
    } catch {
      return null;
    }
  }

  _startIdleTimer() {
    this._stopIdleTimer();
    _idleTimer = setInterval(() => {
      if (this.isIdle()) {
        this._handleSessionExpired('idle_timeout');
      }
    }, 60 * 1000);
  }

  _stopIdleTimer() {
    if (_idleTimer) {
      clearInterval(_idleTimer);
      _idleTimer = null;
    }
  }

  _resetIdleTimer() {
    this._startIdleTimer();
  }

  _startTokenRefresh() {
    this._stopTokenRefresh();
    _tokenRefreshTimer = setInterval(() => {
      const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      if (!token) return;
      const payload = this._decodeToken(token);
      if (payload && payload.exp - Date.now() < 2 * 60 * 1000) {
        this.refreshAccessToken();
      }
    }, 60 * 1000);
  }

  _stopTokenRefresh() {
    if (_tokenRefreshTimer) {
      clearInterval(_tokenRefreshTimer);
      _tokenRefreshTimer = null;
    }
  }

  _handleSessionExpired(reason) {
    logSecurityEvent('session_expired', { reason });
    this.destroySession();
    if (_onSessionExpired) {
      _onSessionExpired(reason);
    }
  }
}

const instance = new SessionManager();
export default instance;
